import React from 'react';
import FieldSkeleton from './IFieldSkeleton';
import {LanguageId} from '../utils/Languages';
import ElementConfiguration from './ElementConfiguration';
import {FieldView} from './FieldView';
import TextField from './TextField';
import DropdownField from './DropdownField';
import {FieldConfigurationQL} from '../graphql/FieldConfigurationQL';
import {FieldType} from './Field';
import TextAreaField from './TextAreaField';
import CheckboxField from './CheckboxField';
import PercentRatingField from './PercentRatingField';
import StarRatingField from './StarRatingField';
import RatingField from './RatingField';

export interface TranslatedWord {
  language: LanguageId
  translation: string
}

export default class FieldConfiguration extends ElementConfiguration {
  id: string
  field: TextField | DropdownField | TextAreaField | CheckboxField | PercentRatingField | StarRatingField | RatingField
  required: boolean
  locked: boolean
  labels: TranslatedWord[]
  editable: boolean

  protected elementName = 'field'

  constructor(input: FieldConfigurationQL) {
    super();
    this.id = input.id;
    switch (input.field.type) {
      case FieldType.Dropdown:
        this.field = new DropdownField(input.field);
        break;
      case FieldType.TextArea:
        this.field = new TextAreaField(input.field);
        break;
      case FieldType.Checkbox:
        this.field = new CheckboxField(input.field);
        break;
      case FieldType.PercentRating:
        this.field = new PercentRatingField(input.field);
        break;
      case FieldType.Rating:
        this.field = new RatingField(input.field);
        break;
      case FieldType.StarRating:
        this.field = new StarRatingField(input.field);
        break;
      default:
        this.field = new TextField(input.field);
    }
    this.required = input.required;
    this.locked = input.locked;
    this.labels = this.field.labels;
    this.editable = input.editable;
  }

  getSystemLabel(): string {
    return this.field.label;
  }

  getLabel(language: LanguageId): string {
    return this.field.labels.find((x) => x.language === language)?.translation || '';
  }


  toSkeleton(): FieldSkeleton {
    return {id: this.id, required: this.required};
  }

  toView(activeLanguage: LanguageId, defaultLanguage: LanguageId, required?: boolean): FieldView {
    const sortableName = this.getLabel(activeLanguage);
    const haveTranslations = this.labels.filter((x) => x.translation.length > 0).map((x) => x.language);
    const translationRequired = haveTranslations.indexOf(defaultLanguage) < 0;
    const label: React.ReactNode = translationRequired ?
      this.getTranslationNotice(sortableName, activeLanguage, defaultLanguage) :
      sortableName.replace(/(<([^>]+)>)/gi, '');

    return {
      id: this.id,
      systemLabel: this.field.label,
      required: required !== undefined ? required : this.required,
      label,
      locked: this.locked,
      editable: this.editable,
      translationRequired,
      highlightError: translationRequired && activeLanguage === defaultLanguage,
    };
  }
}
